import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from "@/components/ui/form/form";
import SubmitButton from "@/components/ui/submit-button";
import { useAuth } from "@/context/auth-context";
import useCreatePinCode from "@/features/register/hooks/useCreatePinCode";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import PinKeyboard from "../../components/_module/pinKeyboard";
import SignupHeader from "../../components/pages/signup/signupHeader";
import { useVerifyAccountStep } from "./useVerifyAccountStep";
import { userKeys } from "@/features/auth/keys";
import { UserInformation } from "@/api/signin";

const formSchema = z.object({
	pin: z
		.string()
		.length(4, { message: "PIN must be exactly 4 digits" })
		.regex(/^\d+$/, { message: "PIN must contain only numbers" }),
});

type FormSchema = z.infer<typeof formSchema>;

const PinPage = () => {
	const queryClient = useQueryClient();
	const { setStep } = useVerifyAccountStep();
	const createPinMutation = useCreatePinCode();
	const { user } = useAuth();

	const form = useForm<FormSchema>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			pin: "",
		},
	});

	const completePinStep = () => {
		queryClient.setQueryData(
			userKeys.userInformation(),
			(oldData: UserInformation): UserInformation => {
				return {
					...oldData,
					location_registration: oldData.location_registration
						? {
								...oldData.location_registration,
								missing_steps:
									oldData.location_registration.missing_steps.filter(
										(step) => step !== "PIN",
									),
							}
						: null,
				};
			},
		);
		void queryClient.refetchQueries({
			queryKey: userKeys.userInformation(),
		});
		next();
	};

	const handleSetPin = (values: FormSchema) => {
		createPinMutation.mutate(
			{
				auth: {
					guestId: user.id,
				},
				pinCode: values.pin,
			},
			{
				onSettled: () => {
					// API may return an error even when the PIN was saved; refetch syncs
					// cache with the server and we always continue (see question.tsx).
					completePinStep();
				},
			},
		);
	};

	const next = () => {
		setStep("question");
	};

	return (
		<div className="flex flex-col items-center justify-between w-full max-w-[800px] mx-auto h-[100dvh] p-6 bg-white">
			<SignupHeader maxProgress={80} isBackButton={false} />
			<div className="mt-4 text-center">
				<h1 className="mb-2 text-2xl font-bold text-textDark">
					Create your PIN
				</h1>
				<p className="text-sm font-medium text-center text-textLight">
					Set a 4-digit code to protect your account.
				</p>
			</div>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleSetPin)}
					className="w-full flex flex-col items-center"
				>
					<FormField
						control={form.control}
						name="pin"
						render={({ field }) => (
							<FormItem className="flex flex-col items-center">
								<FormControl>
									<PinKeyboard
										disabled={createPinMutation.status === "pending"}
										pin={field.value}
										setPin={field.onChange}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<SubmitButton
						className="w-full mt-10 text-white bg-primary-600 max-w-[312px]"
						mutationStatus={createPinMutation.status}
						disabled={createPinMutation.status === "pending"}
					>
						Set up PIN
					</SubmitButton>
				</form>
			</Form>
		</div>
	);
};

export default PinPage;
